import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { paymentApi } from '../services/api.js';
import Navbar from '../components/Navbar';

const getBillingPreferenceLabel = (customId) => {
  const [billingPreference] = (customId || '').split('|');

  switch (billingPreference) {
    case 'DAILY':
      return 'Daily repeat request';
    case 'WEEKLY':
      return 'Weekly repeat request';
    case 'MONTHLY':
      return 'Monthly repeat request';
    default:
      return 'One-time payment';
  }
};

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const capturePayment = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setError('No payment token found');
        setLoading(false);
        return;
      }

      try {
        // Capture the PayPal order
        const response = await paymentApi.captureOrder(token);

        if (response.data.success) {
          setPaymentDetails(response.data);
        } else {
          setError('Payment capture failed');
        }
      } catch (err) {
        console.error('Payment capture error:', err);
        setError(err.response?.data?.details || 'Failed to process payment');
      } finally {
        setLoading(false);
      }
    };

    capturePayment();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#04050c] text-white">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
            <p>Processing your payment...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#04050c] text-white">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center max-w-md">
            <div className="text-red-500 text-5xl mb-4">✗</div>
            <h1 className="text-2xl font-bold mb-4">Payment Failed</h1>
            <p className="text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 rounded-lg transition"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#04050c] text-white">
      <Navbar />
      <div className="flex items-center justify-center h-[calc(100vh-80px)] px-4">
        <div className="text-center max-w-md">
          <div className="text-green-500 text-5xl mb-4">✓</div>
          <h1 className="text-3xl font-bold mb-4">Payment Successful!</h1>
          <p className="text-gray-400 mb-6">Thank you for your purchase</p>

          {paymentDetails && (
            <div className="bg-[#0a0f21] border border-cyan-500/30 rounded-lg p-6 mb-6 text-left space-y-3">
              <div>
                <p className="text-gray-500 text-sm">Order ID</p>
                <p className="text-cyan-400 font-mono break-all">{paymentDetails.orderId}</p>
              </div>
              {paymentDetails.details?.invoice_id && (
                <div>
                  <p className="text-gray-500 text-sm">Invoice Number</p>
                  <p className="text-white font-mono">{paymentDetails.details.invoice_id}</p>
                </div>
              )}
              <div>
                <p className="text-gray-500 text-sm">Status</p>
                <p className="text-green-400 font-semibold">{paymentDetails.status}</p>
              </div>
              {paymentDetails.details?.description && (
                <div>
                  <p className="text-gray-500 text-sm">Description</p>
                  <p className="text-white">{paymentDetails.details.description}</p>
                </div>
              )}
              {paymentDetails.details?.custom_id && (
                <div>
                  <p className="text-gray-500 text-sm">Billing Preference</p>
                  <p className="text-white font-semibold">
                    {getBillingPreferenceLabel(paymentDetails.details.custom_id)}
                  </p>
                </div>
              )}
              {paymentDetails.details?.amount && (
                <div>
                  <p className="text-gray-500 text-sm">Amount</p>
                  <p className="text-white font-semibold">
                    ${paymentDetails.details.amount.value} {paymentDetails.details.amount.currency_code}
                  </p>
                </div>
              )}
            </div>
          )}

          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 rounded-lg transition"
          >
            Return to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
